<template>
  <div class="landing">
    <div class="landing__bg-grid"></div>
    <header class="landing__header">
      <div class="brand">
        <div class="logo">SC</div>
        <div class="brand-text">
          <div class="title">Sheet Creator</div>
          <div class="subtitle">Design sheets and deploy them instantly</div>
        </div>
      </div>
      <nav class="nav">
        <button class="nav__link" type="button" v-if="false">Projects</button>
        <button class="nav__link" type="button" @click="goToPreRegister">Pre-register</button>
      </nav>
    </header>

    <main class="landing__main">
      <section class="hero">
        <div class="hero__copy">
          <p class="eyebrow">Design tool for fast outcomes</p>
          <h1>Design sheets like a canvas, ship them in a click.</h1>
          <p class="hero__subtitle">
            Sheet Creator is a frame-based designing tool for planners, trackers and game sheets.
            Sketch layouts on a modern canvas, then export or deploy the result as soon as you’re done.
          </p>
          <p class="hero__badge">Editor currently in development — expect changes.</p>
          <div class="hero__actions">
            <button class="btn primary" type="button" @click="goToEditor">
              Open Editor
            </button>
            <button class="btn ghost" type="button" @click="goToPreRegister">
              Pre-register
            </button>
          </div>
          <p class="hero__meta">
            No account required to explore. Your work stays on this device.
          </p>
        </div>

        <div style="position: relative;">
            <div class="hero__preview" aria-hidden="true">
              <div class="preview-window">
                <div class="preview-titlebar">
                  <span class="dot dot--red"></span>
                  <span class="dot dot--amber"></span>
                  <span class="dot dot--green"></span>
                  <span class="title">User project.sheets</span>
                </div>
                <div class="preview-body">
                  <aside class="preview-sidebar">
                    <div class="sidebar__item sidebar__item--active"></div>
                    <div class="sidebar__item"></div>
                    <div class="sidebar__item"></div>
                    <div class="sidebar__item"></div>
                  </aside>
                  <div class="preview-canvas">
                    <div class="preview-grid">
                      <div v-for="row in 4" :key="`row-${row}`" class="preview-row">
                        <div v-for="col in 6" :key="`cell-${row}-${col}`" class="preview-cell"></div>
                      </div>
                    </div>
                    <div class="preview-toolbar">
                      <div class="pill pill--active"></div>
                      <div class="pill"></div>
                      <div class="pill"></div>
                      <div class="pill pill--ghost"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="actual" aria-hidden="true">
              <div class="preview-window">
                <div class="preview-titlebar">
                  <span class="dot dot--red"></span>
                  <span class="dot dot--amber"></span>
                  <span class="dot dot--green"></span>
                  <span class="title">User project.sheets</span>
                </div>
                <div class="preview-body">
                  <aside class="preview-sidebar">
                    <div class="sidebar__item sidebar__item--active"></div>
                    <div class="sidebar__item"></div>
                    <div class="sidebar__item"></div>
                    <div class="sidebar__item"></div>
                  </aside>
                  <div class="preview-canvas">
                    <div class="preview-grid">
                      <div v-for="row in 4" :key="`row-${row}`" class="preview-row">
                        <div v-for="col in 6" :key="`cell-${row}-${col}`" class="preview-cell"></div>
                      </div>
                    </div>
                    <div class="preview-toolbar">
                      <div class="pill pill--active"></div>
                      <div class="pill"></div>
                      <div class="pill"></div>
                      <div class="pill pill--ghost"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
        </div>
      </section>

      <section class="features" aria-label="Highlights">
        <div class="feature">
          <h2>Design-first workflow</h2>
          <p>
            Think in frames, sections and structure. Move, group and align elements the way
            you would in a modern design tool, with output ready for paper or screen.
          </p>
        </div>
        <div class="feature">
          <h2>Canvas built for layout</h2>
          <p>
            Under the hood is a vector canvas with rulers, snapping and precise positioning
            so every frame, label and line lands exactly where you want it.
          </p>
        </div>
        <div class="feature">
          <h2>Always yours</h2>
          <p>
            Projects are stored locally by default. Export to print, share or
            move between machines when you’re ready.
          </p>
        </div>
      </section>
    </main>

    <footer class="landing__footer">
      <span>&copy; {{ year }} Sheet Creator</span>
    </footer>
  </div>
</template>

<script>
export default {
    name: 'LandingView',
    computed: {
        year() {
            return new Date().getFullYear()
        },
        isDesktop() {
            if (typeof navigator === 'undefined') return true

            const ua = (navigator.userAgent || navigator.vendor || window.opera || '').toLowerCase()
            return !/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua)
        }
    },
    methods: {
        goToEditor() {
            this.$store.dispatch('events/sendUserEvent', {event_action: 'open_editor.clicked'})

            this.$router.push({ name: 'editor' })
        },
        goToPreRegister() {
            this.$store.dispatch('events/sendUserEvent', {event_action: 'preregister.clicked'})

            this.$router.push({ name: 'pre-register' })
        }
    }
}
</script>

<style scoped>
.landing {
    min-height: 100dvh;
    display: grid;
    grid-template-rows: auto 1fr auto;
    padding: 24px 32px;
    background:
        radial-gradient(1100px 800px at 10% -10%, #1a2a5a30, transparent),
        radial-gradient(1000px 700px at 110% 120%, #3b82f620, transparent),
        linear-gradient(180deg, var(--panel), var(--panel-2));
    background-size: 140% 140%;
    /* animation: landingGradientDrift 2s ease-in-out infinite alternate; */
    background-position: 0% 0%;
    color: var(--text);
    font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji";
    position: relative;
    overflow: hidden;
}

.landing__bg-grid {
    position: absolute;
    inset: 0;
    background:
        linear-gradient(0deg, transparent 24%, rgba(255, 255, 255, 0.03) 25%, rgba(255, 255, 255, 0.03) 26%, transparent 27%, transparent 74%, rgba(255, 255, 255, 0.03) 75%, rgba(255, 255, 255, 0.03) 76%, transparent 77%, transparent),
        linear-gradient(90deg, transparent 24%, rgba(255, 255, 255, 0.03) 25%, rgba(255, 255, 255, 0.03) 26%, transparent 27%, transparent 74%, rgba(255, 255, 255, 0.03) 75%, rgba(255, 255, 255, 0.03) 76%, transparent 77%, transparent);
    background-size: 40px 40px;
    opacity: 0.35;
    pointer-events: none;
}

.landing__header,
.landing__main,
.landing__footer {
    position: relative;
    z-index: 1;
}

.landing__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: var(--accent);
  display: grid;
  place-items: center;
  color: #fff;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.brand-text .title {
  font-weight: 700;
  letter-spacing: 0.2px;
}

.brand-text .subtitle {
  color: var(--muted);
  font-size: 12px;
  margin-top: 2px;
}

.nav {
  display: flex;
  align-items: center;
  gap: 12px;
}

.nav__link {
  border: none;
  background: transparent;
  color: var(--muted);
  font-size: 14px;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 999px;
}

.nav__link:hover {
  color: var(--text);
  background: rgba(148, 163, 184, 0.12);
}

.landing__main {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  align-items: center;
  gap: 40px;
  margin-top: 40px;
}

.hero {
  display: grid;
  gap: 24px;
}

.hero__copy {
  display: grid;
  gap: 16px;
}

.eyebrow {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--muted);
}

h1 {
  font-size: clamp(32px, 5vw, 42px);
  line-height: 1.1;
}

.hero__subtitle {
  color: var(--muted);
  font-size: 15px;
  max-width: 460px;
}

.hero__badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.55);
  background: rgba(15, 23, 42, 0.75);
  color: var(--muted);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-top: 4px;
}

.hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 4px;
}

.hero__meta {
  color: var(--muted);
  font-size: 13px;
}

.btn {
  height: 38px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--panel-2);
  color: var(--text);
  cursor: pointer;
  padding: 0 16px;
  font-weight: 600;
  font-size: 14px;
}

.btn.primary {
  background: var(--accent);
  border-color: var(--accent);
  color: #fff;
}

.btn.ghost {
  background: transparent;
}

.btn:hover {
  filter: brightness(1.05);
}

.hero__preview {
    display: flex;
    justify-content: flex-end;
    visibility: hidden;
    background-color: red;
}
.actual {
    display: flex;
    justify-content: flex-end;
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    animation-name: preview_fadein;
    animation-duration: 3s;
    animation-direction: forwards;
    animation-play-state: running;
    animation-fill-mode: forwards;
}

.preview-window {
  width: 100%;
  max-width: 460px;
  border-radius: 18px;
  border: 1px solid var(--border);
  background: var(--panel);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6);
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr;
}

.preview-titlebar {
  height: 34px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  background: var(--titlebar);
  border-bottom: 1px solid rgba(15, 23, 42, 0.8);
  font-size: 12px;
  color: var(--muted);
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #4b5563;
}

.dot--red {
  background: #f97373;
}

.dot--amber {
  background: #facc15;
}

.dot--green {
  background: #4ade80;
}

.preview-body {
  display: grid;
  grid-template-columns: 52px 1fr;
  min-height: 220px;
}

.preview-sidebar {
  background: var(--panel-2);
  border-right: 1px solid var(--border);
  padding: 10px 6px;
  display: grid;
  gap: 8px;
}

.sidebar__item {
  height: 26px;
  border-radius: 8px;
  background: rgba(148, 163, 184, 0.16);
}

.sidebar__item--active {
  background: rgba(78, 161, 255, 0.5);
}

.preview-canvas {
  padding: 12px 14px 14px;
  background: radial-gradient(circle at top left, #1e293b, #020617);
  display: grid;
  grid-template-rows: 1fr auto;
  gap: 10px;
}

.preview-grid {
  border-radius: 12px;
  background: var(--canvas);
  border: 1px solid var(--border);
  padding: 10px 12px;
  display: grid;
  gap: 6px;
}

.preview-row {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 6px;
}

.preview-cell {
  height: 18px;
  border-radius: 4px;
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: rgba(15, 23, 42, 0.8);
}

.preview-toolbar {
  display: flex;
  gap: 8px;
}

.pill {
  height: 22px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.5);
  background: rgba(15, 23, 42, 0.9);
  flex: 0 0 48px;
}

.pill--active {
  background: var(--accent);
  border-color: var(--accent);
}

.pill--ghost {
  flex: 1 1 auto;
  opacity: 0.6;
}

.features {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
  margin-top: 40px;
}

.feature {
  padding: 18px 18px 20px;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: linear-gradient(145deg, rgba(15, 23, 42, 0.9), rgba(30, 64, 175, 0.4));
  backdrop-filter: blur(12px);
}

.feature h2 {
  font-size: 16px;
  margin-bottom: 6px;
}

.feature p {
  font-size: 13px;
  color: var(--muted);
}

.landing__footer {
  margin-top: 32px;
  font-size: 12px;
  color: var(--muted);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

@keyframes landingGradientDrift {
    0% {
        background-position: 0% 0%;
    }
    50% {
        background-position: 30% 20%;
    }
    100% {
        background-position: 60% 40%;
    }
}

@keyframes preview_fadein {
    0% {
        left: -100%;
    }
    100% {
        left: 0%;
    }
}

@media (max-width: 1024px) {
  .landing {
    padding: 20px 18px;
  }

  .landing__main {
    grid-template-columns: 1fr;
    align-items: flex-start;
  }

  .hero__preview {
    justify-content: flex-start;
  }

  .features {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .landing {
    padding: 16px 14px 18px;
  }

  .landing__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .hero__actions {
    width: 100%;
  }

  .btn {
    flex: 1 1 auto;
    text-align: center;
  }

  .features {
    grid-template-columns: 1fr;
  }

  .landing__footer {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
